<script setup lang="ts">
	import { MzButton, MzTitle } from '@monizze/monizze-components';
	import { useI18n } from 'vue-i18n';
	import { useRoute, useRouter } from 'vue-router';
	import { useAuthStore } from '@/stores/auth.ts';
	import { onBeforeMount, ref, Ref } from 'vue';
	import { useUserStore } from '@/stores/user.ts';

	const { t } = useI18n();
	const authStore = useAuthStore();
	const userStore = useUserStore();
	const router = useRouter();
	const route = useRoute();
	const isLogin: Ref<boolean> = ref(false);

	const unusedKeyCount: Ref<number> = ref(0);

	onBeforeMount(async () => {
		await userStore.refreshUser();
		unusedKeyCount.value = userStore.user.totpRecovery?.filter((key) => key.used_at === null).length || 0;

		if (!route.query.mfaId) {
			authStore.logout();
		}

		if (route.query.isLogin === 'true') {
			isLogin.value = true;
		}
	});

	const regenerate = async () => {
		await userStore.regenerateRecovery();
		router.push({
			name: 'setup-totp-success',
			query: {
				isRegenerated: 'true',
				...(typeof route.query.action === 'string' && { action: route.query.action }),
				...(typeof route.query.redirect === 'string' && { redirect: route.query.redirect }),
				...(typeof route.query.targetMfaId !== 'undefined' && { targetMfaId: route.query.targetMfaId }),
			},
		});
	};

	const reset = async () => {
		try {
			// Recovery re-sets up the TOTP right after, so force the delete past the keep-one-strong-method guard.
			await userStore.deleteMfa(parseInt(route.query.mfaId as string), undefined, true);
			await router.push({ name: 'setup-mfa-totp', query: { reset: 'true' } });
		} catch (e) {
			console.log(e);
		}
	};

	const skip = async () => {
		if (typeof route.query.redirect === 'string') {
			router.push({
				name: route.query.redirect,
				query: {
					...(typeof route.query.action === 'string' && { action: route.query.action }),
					...(typeof route.query.targetMfaId !== 'undefined' && { targetMfaId: route.query.targetMfaId }),
				},
			});
			return;
		}

		if (!!(isLogin.value ? sessionStorage.getItem('loginPayload') : route.query.payload) && typeof route.query.method === 'string') {
			router.push(useAuthStore().targetUrl);
			return;
		}

		router.push('home');
		return;
	};
</script>

<template>
	<div class="flex flex-col gap-6">
		<div class="flex flex-col gap-2">
			<mz-title type="h1" :text="t('uzzer.mfa.selection.title')" />
			<p>{{ t('uzzer.force_mfa.subtitle') }}</p>
		</div>
		<div
			class="bg-bg-box-default border border-border-default rounded-md p-6 sm:p-10 flex md:flex-row flex-col gap-6 sm:gap-10 items-center"
		>
			<div class="md:w-1/2 w-full flex justify-center">
				<img src="/Mfa/totpRecoverySuccess.svg" alt="Totp recovery success image" />
			</div>
			<div class="md:w-1/2 w-full flex flex-col gap-6">
				<mz-title type="h2" :text="t('user.mfa.totp-recovery.validation.title')" />
				<template v-if="unusedKeyCount > 0">
					<i18n-t keypath="user.mfa.totp-recovery.validation.success" tag="p">
						<template #x>
							<span class="font-bold">{{ unusedKeyCount }}</span>
						</template>
					</i18n-t>
					<p>{{ t('user.mfa.totp-recovery.validation.lost-access') }}</p>
					<p>{{ t('user.mfa.totp-recovery.validation.ignore') }}</p>
					<div class="flex md:flex-row flex-col gap-2">
						<mz-button
							class="w-full md:w-auto"
							variant="primary"
							:label="t('user.mfa.totp-recovery.validation.reset-button')"
							width="auto"
							@click="reset"
						/>
						<mz-button
							class="w-full md:w-auto"
							variant="outline"
							:label="t('user.mfa.totp-recovery.validation.skip')"
							width="auto"
							@click="skip"
						/>
					</div>
				</template>
				<template v-else>
					<p>{{ t('user.mfa.totp-recovery.validation.no-keys-left') }}</p>
					<p>{{ t('user.mfa.totp-recovery.validation.regenerate') }}</p>
					<p>{{ t('user.mfa.totp-recovery.validation.lost-access') }}</p>
					<p>{{ t('user.mfa.totp-recovery.validation.ignore') }}</p>
					<div class="flex md:flex-row flex-col gap-2">
						<mz-button
							class="w-full md:w-auto"
							variant="primary"
							:label="t('user.mfa.totp-recovery.validation.regenerate-button')"
							width="auto"
							@click="regenerate"
						/>
						<mz-button
							class="w-full md:w-auto"
							variant="outline"
							:label="t('user.mfa.totp-recovery.validation.reset-button')"
							width="auto"
							@click="reset"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>
